import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedApiContext } from '../api/useApiClient';
import { Domain, DomainsResponse } from '../types';
import { AsyncState, useAsync } from './useAsync';
import { useAsyncEffect } from './useAsyncEffect';

const BATCH_SIZE = 5;

interface TenantDomainsCtx {
  tenantDomains: Domain[];
  loadTenantDomains: AsyncState<void, () => Promise<void>>;
  checkDomain: AsyncState<void, (domainId: string) => Promise<void>>;
  addDomain: AsyncState<void, (domainName: string) => Promise<void>>;
  deleteDomain: AsyncState<void, (domainId: string) => Promise<void>>;
}

const TenantDomainsContext = createContext<TenantDomainsCtx>({} as TenantDomainsCtx);

export const TenantDomainsProvider: React.FC = ({ children }) => {
  const tenantDomainsHook = useTenantDomainsHook();
  return <TenantDomainsContext.Provider value={{ ...tenantDomainsHook }}>{children}</TenantDomainsContext.Provider>;
};

export function useTenantDomains(): TenantDomainsCtx {
  return useContext(TenantDomainsContext);
}

const useTenantDomainsHook = (): TenantDomainsCtx => {
  const [tenantDomains, setTenantDomains] = useState<Domain[]>([]);
  const { t } = useTranslation();
  const apiClient = useAuthenticatedApiContext();

  const loadTenantDomains = useAsync(
    useCallback(async (): Promise<void> => {
      const domains: Domain[] = [];
      let nextPageCursor: string | null = null;
      do {
        // eslint-disable-next-line no-await-in-loop
        const resp: DomainsResponse = await apiClient.getDomains({
          limit: BATCH_SIZE,
          cursor: nextPageCursor,
        });
        domains.push(...resp.data);
        nextPageCursor = resp.nextPageCursor;
      } while (nextPageCursor);
      setTenantDomains(domains);
    }, [apiClient]),
    { autoReset: true }
  );
  useAsyncEffect(loadTenantDomains, []);

  const checkDomain = useAsync(
    useCallback(
      async (domainId: string): Promise<void> => {
        await apiClient.checkDomain(domainId).catch(() => {
          throw new Error(t('Failed to refresh Domain Verification status'));
        });
        await loadTenantDomains.execute();
      },
      [loadTenantDomains, t, apiClient]
    ),
    { autoReset: true }
  );

  const addDomain = useAsync(
    useCallback(
      async (domainName: string): Promise<void> => {
        await apiClient.addDomain(domainName).catch(() => {
          throw new Error(t('Failed to add Domain'));
        });
        await loadTenantDomains.execute();
      },
      [loadTenantDomains, t, apiClient]
    ),
    { autoReset: true }
  );

  const deleteDomain = useAsync(
    useCallback(
      async (domainId: string): Promise<void> => {
        await apiClient.deleteDomain(domainId).catch(() => {
          throw new Error(t('Failed to delete Domain'));
        });
        await loadTenantDomains.execute();
      },
      [loadTenantDomains, t, apiClient]
    ),
    { autoReset: true }
  );

  return { loadTenantDomains, checkDomain, addDomain, deleteDomain, tenantDomains };
};
